import {useCallback, useEffect, useRef, useState} from 'react'
import {RefreshCw, ExternalLink} from 'lucide-react'
import {Text} from '@/components/tailwind/text'
import {Badge} from '@/components/tailwind/badge'
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/tailwind/table'
import {useAdminTranslation} from "@/i18n/hooks"
import {useAuth} from '@/context/use-auth'
import LoadingSpinner from "@/components/loader-spinner"
import {getEmailSendLogs} from '@/services/email-send-log-services'
import type {EmailSendLogEntry} from '@/services/email-send-log-services'
import {formatDateTimeString} from '@/lib/utils'
import {Subheading} from '@/components/tailwind/heading'
import {Link} from 'react-router-dom'
import log from '@/lib/logger'

const PREVIEW_SIZE = 10

function getStatusBadgeColor(status: string): "green" | "red" | "zinc" {
    switch (status) {
        case 'SUCCESS':
            return 'green'
        case 'FAILURE':
            return 'red'
        default:
            return 'zinc'
    }
}

export function SectionEmailSendLog() {
    const {t} = useAdminTranslation()
    const {token} = useAuth()

    const [loading, setLoading] = useState(true)
    const [entries, setEntries] = useState<EmailSendLogEntry[]>([])
    const [totalElements, setTotalElements] = useState(0)
    const sectionRef = useRef<HTMLDivElement>(null)
    const lastFetchRef = useRef<number>(0)

    const fetchEmailSendLogs = useCallback(() => {
        if (!token) return
        setLoading(true)
        getEmailSendLogs(token, 0, PREVIEW_SIZE)
            .then((result) => {
                if (result) {
                    setEntries(result.entries)
                    setTotalElements(result.totalElements)
                }
            })
            .catch((err) => {
                log.error('Failed to load email send logs:', err)
                setEntries([])
            })
            .finally(() => {
                setLoading(false)
                lastFetchRef.current = Date.now()
            })
    }, [token])

    useEffect(() => {
        fetchEmailSendLogs()
    }, [fetchEmailSendLogs])

    useEffect(() => {
        const element = sectionRef.current
        if (!element) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && Date.now() - lastFetchRef.current > 5000) {
                    fetchEmailSendLogs()
                }
            },
            {threshold: 0.1},
        )
        observer.observe(element)
        return () => observer.disconnect()
    }, [fetchEmailSendLogs])

    return (
        <div ref={sectionRef}>
            <div className="flex items-center justify-between">
                <div>
                    <Subheading>{t('adminSettings.emailSendLog.title')}</Subheading>
                    <Text className="mt-1">{t('adminSettings.emailSendLog.description')}</Text>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={fetchEmailSendLogs}
                        disabled={loading}
                        className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
                        title={t('adminSettings.emailSendLog.refresh')}
                    >
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="mt-4">
                    <LoadingSpinner />
                </div>
            ) : entries.length === 0 ? (
                <Text className="mt-4 text-zinc-500 dark:text-zinc-400">
                    {t('adminSettings.emailSendLog.noEntries')}
                </Text>
            ) : (
                <>
                    <div className="mt-4">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.timestamp')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.recipient')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.subject')}</TableHeader>
                                    <TableHeader>{t('adminSettings.emailSendLog.columns.status')}</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {entries.map((entry) => (
                                    <TableRow key={entry.id}>
                                        <TableCell className="whitespace-nowrap text-sm">
                                            {formatDateTimeString(entry.createdAt)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {entry.recipientEmail}
                                        </TableCell>
                                        <TableCell className="text-sm max-w-xs truncate" title={entry.subject}>
                                            {entry.subject}
                                        </TableCell>
                                        <TableCell>
                                            <Badge color={getStatusBadgeColor(entry.status)}>
                                                {t(`adminSettings.emailSendLog.status.${entry.status}`)}
                                            </Badge>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    {totalElements > 0 && (
                        <div className="mt-3 text-center">
                            <Link
                                to="/admin/email-send-log"
                                className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100 transition-colors"
                            >
                                {totalElements > PREVIEW_SIZE
                                    ? t('adminSettings.emailSendLog.showAll', {count: totalElements})
                                    : t('adminSettings.emailSendLog.title')}
                                <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}
